import React, { useState } from 'react';
import SearchableSelect from './SearchableSelect';

/**
 * Survey search filter panel.
 *
 * Date period buttons and custom date range call onDateRangeChange — the parent
 * should re-fetch surveys from the API when this fires.
 *
 * Tag selection calls onTagsChange — the parent filters the already-fetched list
 * client-side (no API call). A server-side tag search endpoint is not yet available.
 *
 * NOTE: modifiedAfter is not yet supported by the backend; the parent should
 * apply it as a client-side filter on the returned results until the endpoint
 * is updated.
 *
 * @param {string[]}  availableTags       All existing tag strings for the dropdown
 * @param {Function}  onDateRangeChange   Called with { modifiedAfter, modifiedBefore }
 * @param {Function}  onTagsChange        Called with string[] of selected tags
 * @param {boolean}   isSearching         Shows spinner / disables buttons while fetching
 */
export default function SurveySearchFilters({
  availableTags = [],
  onDateRangeChange,
  onTagsChange,
  isSearching = false
}) {
  const [activePeriod, setActivePeriod] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  const computeDateParams = (period, from, to) => {
    if (period === 'all') return { modifiedAfter: null, modifiedBefore: null };
    if (period === 'custom') {
      return {
        modifiedAfter: from ? `${from}T00:00:00.000Z` : null,
        modifiedBefore: to ? `${to}T23:59:59.999Z` : null
      };
    }
    const months = { '1m': 1, '3m': 3, '6m': 6, '12m': 12 }[period];
    const after = new Date();
    after.setMonth(after.getMonth() - months);
    return { modifiedAfter: after.toISOString(), modifiedBefore: null };
  };

  const handlePeriodClick = (period) => {
    setActivePeriod(period);
    setShowCustom(period === 'custom');
    if (period !== 'custom') {
      onDateRangeChange(computeDateParams(period, customFrom, customTo));
    }
  };

  const handleCustomApply = () => {
    onDateRangeChange(computeDateParams('custom', customFrom, customTo));
  };

  const handleTagsChange = (newTags) => {
    setSelectedTags(newTags);
    onTagsChange(newTags);
  };

  const removeTag = (tag) => handleTagsChange(selectedTags.filter(t => t !== tag));

  const PERIODS = [
    { key: 'all',    label: 'All' },
    { key: '1m',     label: 'Last month' },
    { key: '3m',     label: '3 months' },
    { key: '6m',     label: '6 months' },
    { key: '12m',    label: '1 year' },
    { key: 'custom', label: 'Custom…' },
  ];

  return (
    <div className="border rounded p-3 mb-3 bg-light">

      {/* ── Date filter ── */}
      <div className="mb-2">
        <span className="form-label  d-block mb-1">Filter by date modified:</span>
        <div className="d-flex flex-wrap gap-1">
          {PERIODS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${activePeriod === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handlePeriodClick(key)}
              disabled={isSearching}
            >
              {isSearching && activePeriod === key && key !== 'custom'
                ? <><span className="spinner-border spinner-border-sm me-1" />Loading…</>
                : label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Custom date range ── */}
      {showCustom && (
        <div className="d-flex flex-wrap align-items-end gap-2 mb-2 ps-1">
          <div>
            <label className="form-label small mb-1">From:</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={customFrom}
              onChange={e => setCustomFrom(e.target.value)}
              max={customTo || undefined}
            />
          </div>
          <div>
            <label className="form-label small mb-1">To:</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={customTo}
              onChange={e => setCustomTo(e.target.value)}
              min={customFrom || undefined}
            />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleCustomApply}
            disabled={isSearching || (!customFrom && !customTo)}
          >
            {isSearching
              ? <><span className="spinner-border spinner-border-sm me-1" />Searching…</>
              : 'Apply dates'}
          </button>
        </div>
      )}

      {/* ── Tag filter ── */}
      <div>
        <span className="form-label  d-block mb-1">
          Filter by tags:
        </span>
        <SearchableSelect
          options={availableTags}
          value={selectedTags}
          onChange={handleTagsChange}
          placeholder="Pick a tag to filter…"
          getLabel={tag => tag}
          getValue={tag => tag}
          multiple={true}
          allowCreate={false}
          maxHeight={200}
        />
        {selectedTags.length > 0 && (
          <div className="d-flex flex-wrap gap-1 mt-2">
            {selectedTags.map(tag => (
              <span
                key={tag}
                className="badge bg-secondary d-inline-flex align-items-center gap-1"
                style={{ fontSize: '0.85em' }}
              >
                {tag}
                <button
                  type="button"
                  className="btn-close btn-close-white ms-1"
                  style={{ fontSize: '0.55em' }}
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove tag ${tag}`}
                />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
